import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, GitBranch, Printer, Bookmark, FileText,
  HardDriveDownload, CheckCircle2, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const abs = (u) =>
  u?.startsWith("http") || u?.startsWith("/demo") ? u : `${API}${u}`;

export async function svgToPngDownload(svgUrl, filename) {
  const res = await fetch(svgUrl, { credentials: "include" });
  const svgText = await res.text();
  const blob = new Blob([svgText], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  await new Promise((ok, err) => { img.onload = ok; img.onerror = err; img.src = url; });
  const canvas = document.createElement("canvas");
  canvas.width = 1720; canvas.height = 1120;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = filename;
  a.click();
}

export function downloadFile(url, filename) {
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click();
}

export async function saveExecToDevice(exec, slug = "mindstein") {
  downloadFile(abs(exec.pdf_url), `Executive-Summary-${slug}.pdf`);
  try { await svgToPngDownload(abs(exec.svg_url), `Diagram-${slug}.png`); }
  catch { window.open(abs(exec.svg_url), "_blank"); }
  toast.success("SAVED_TO_DEVICE — PDF + diagram downloaded");
}

export default function SummaryDiagramResults({
  exec, topic = "synthesis", demo = false, showSaveToDevice = false,
  messageId = "", sessionId = "", preview = "",
}) {
  const [tab, setTab] = useState("diagram");
  const [saved, setSaved] = useState(!demo); // live runs auto-save to library
  const [queued, setQueued] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!exec) return null;
  const pdfUrl = abs(exec.pdf_url);
  const svgUrl = abs(exec.svg_url);
  const slug = (exec.id || "demo").slice(0, 8);

  const btn = "inline-flex items-center gap-1.5 px-3 h-9 text-[10px] font-mono uppercase tracking-wider border transition-colors";

  const printPdf = () => {
    const w = window.open(pdfUrl, "_blank");
    if (w) setTimeout(() => { try { w.print(); } catch (e) {} }, 1200);
  };
  const saveToLib = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/exec-summary/${exec.id}/save`, {}, { withCredentials: true });
      setSaved(true);
      toast.success("Saved to Research Library", { description: topic.slice(0, 60) });
    } catch (e) { toast.error("SAVE_FAILED"); }
    setBusy(false);
  };
  const downloadDiagram = async () => {
    try { await svgToPngDownload(svgUrl, `Diagram-${slug}.png`); }
    catch { window.open(svgUrl, "_blank"); }
  };
  const saveForLater = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/read-later`, {
        message_id: messageId, session_id: sessionId,
        topic: topic.slice(0, 200), preview: preview.slice(0, 500),
        exec_summary: exec,
      }, { withCredentials: true });
      setQueued(true);
      toast.success(r.data?.already_queued ? "ALREADY_IN_QUEUE" : "SAVED_FOR_LATER — added to your reading queue", {
        action: { label: "OPEN_QUEUE", onClick: () => (window.location.href = "/read-later") },
      });
    } catch (e) { toast.error("QUEUE_FAILED", { description: e?.response?.data?.detail || "" }); }
    setBusy(false);
  };

  return (
    <div data-testid="summary-diagram-results" className="border border-amber-400/40 bg-[#0A0A0A]">
      {/* distinct tab header */}
      <div className="flex items-end justify-between border-b border-amber-400/40 bg-amber-400/[0.04] pl-0 pr-3">
        <div data-testid="sdr-tab-title"
             className="px-4 py-2 -mb-px border border-amber-400 border-b-transparent bg-[#0A0A0A] text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-amber-300">
          SUMMARY_AND_DIAGRAM_RESULTS
        </div>
        <span className="pb-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-emerald-300">
          <CheckCircle2 size={10} /> {demo ? "AUTO_GENERATED_EVERY_RUN" : "AUTO_SAVED_TO_LIBRARY"}
        </span>
      </div>

      {/* preview switcher */}
      <div className="px-3 pt-3 flex items-center gap-2">
        <button data-testid="sdr-tab-diagram" onClick={() => setTab("diagram")}
                className={"px-3 h-7 text-[10px] font-mono uppercase tracking-wider border flex items-center gap-1 " +
                  (tab === "diagram" ? "border-emerald-400 bg-emerald-400/10 text-emerald-200" : "border-[#27272A] text-zinc-500 hover:text-white")}>
          <GitBranch size={11} /> DIAGRAM
        </button>
        <button data-testid="sdr-tab-pdf" onClick={() => setTab("pdf")}
                className={"px-3 h-7 text-[10px] font-mono uppercase tracking-wider border flex items-center gap-1 " +
                  (tab === "pdf" ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-[#27272A] text-zinc-500 hover:text-white")}>
          <FileText size={11} /> PDF_PREVIEW
        </button>
      </div>

      <div className="p-3">
        {tab === "diagram" && (
          <div data-testid="sdr-preview-diagram" className="bg-white p-2 overflow-auto">
            <img src={svgUrl} alt="Structural synthesis diagram — grounded connections only"
                 className="w-full max-h-[420px] object-contain" />
          </div>
        )}
        {tab === "pdf" && (
          <iframe data-testid="sdr-preview-pdf" title="Executive Summary PDF" src={pdfUrl}
                  className="w-full h-[420px] bg-white border-0" />
        )}
      </div>

      {/* clear action buttons */}
      <div className="flex flex-wrap gap-2 px-3 pb-3">
        <a data-testid="sdr-download-pdf" href={pdfUrl} download={`Executive-Summary-${slug}.pdf`}
           className={btn + " border-amber-400/60 text-amber-300 hover:bg-amber-500/10"}>
          <Download size={12} /> Download PDF
        </a>
        <button data-testid="sdr-download-diagram" onClick={downloadDiagram}
                className={btn + " border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10"}>
          <GitBranch size={12} /> Download Diagram
        </button>
        <button data-testid="sdr-print" onClick={printPdf}
                className={btn + " border-[#27272A] text-zinc-300 hover:text-white hover:border-zinc-500"}>
          <Printer size={12} /> Print
        </button>
        {demo ? (
          <Link data-testid="sdr-save-library" to="/register"
                className={btn + " border-[#27272A] text-zinc-300 hover:text-white hover:border-zinc-500"}>
            <Bookmark size={12} /> Save to Library
          </Link>
        ) : (
          <button data-testid="sdr-save-library" onClick={saveToLib} disabled={busy || saved}
                  className={btn + " border-[#27272A] text-zinc-300 hover:text-white hover:border-zinc-500" + (saved ? " opacity-50" : "")}>
            <Bookmark size={12} /> {saved ? "Saved to Library ✓" : "Save to Library"}
          </button>
        )}
        {demo ? (
          <Link data-testid="sdr-save-later" to="/register"
                title="Save for Later — a temporary reading queue, separate from the permanent Research Library"
                className={btn + " border-purple-400/60 text-purple-300 hover:bg-purple-500/10"}>
            <Clock size={12} /> Save for Later
          </Link>
        ) : (
          <button data-testid="sdr-save-later" onClick={saveForLater} disabled={busy || queued}
                  title="Save for Later — a temporary reading queue, separate from the permanent Research Library"
                  className={btn + " border-purple-400/60 text-purple-300 hover:bg-purple-500/10" + (queued ? " opacity-50" : "")}>
            <Clock size={12} /> {queued ? "In Queue ✓" : "Save for Later"}
          </button>
        )}
        {showSaveToDevice && (
          <button data-testid="sdr-save-device" onClick={() => saveExecToDevice(exec, slug)}
                  className={btn + " border-sky-400/60 text-sky-300 hover:bg-sky-500/10"}>
            <HardDriveDownload size={12} /> Save to Device
          </button>
        )}
      </div>
    </div>
  );
}
